export interface ProductDetailSection {
  id: string;
  itemCount: number;
  portal?: 'admin' | 'renter';
}

export interface ProductVisualBlock {
  src: string;
  titleKey: string;
  textKey: string;
}

export interface ProductFeature {
  id: string;
  icon: string;
  titleKey: string;
  leadKey: string;
  detailKey: string;
  bulletKeys: string[];
  screenshot: string;
  screenshotAltKey: string;
  adminVisualBlocks?: ProductVisualBlock[];
  detailIntroKey: string;
  detailSections: ProductDetailSection[];
}

function visualBlock(featureId: string, visualId: string, imageId: string): ProductVisualBlock {
  const base = `PRODUCTS.DETAIL.${featureId.toUpperCase()}.SECTIONS.ADMIN.VISUAL_${visualId}`;
  return {
    src: productScreenshot(imageId),
    titleKey: `${base}_TITLE`,
    textKey: `${base}_TEXT`,
  };
}

function portalSections(itemCount = 4): ProductDetailSection[] {
  return [
    { id: 'ADMIN', itemCount, portal: 'admin' },
    { id: 'RENTER', itemCount, portal: 'renter' },
  ];
}

function productScreenshot(id: string): string {
  return `assets/images/products/${id}.png`;
}

export const PRODUCT_FEATURES: ProductFeature[] = [
  {
    id: 'dashboard',
    icon: 'lni-dashboard',
    titleKey: 'PRODUCTS.FEATURES.DASHBOARD.TITLE',
    leadKey: 'PRODUCTS.FEATURES.DASHBOARD.LEAD',
    detailKey: 'PRODUCTS.FEATURES.DASHBOARD.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.DASHBOARD.BULLET_1',
      'PRODUCTS.FEATURES.DASHBOARD.BULLET_2',
      'PRODUCTS.FEATURES.DASHBOARD.BULLET_3',
      'PRODUCTS.FEATURES.DASHBOARD.BULLET_4',
      'PRODUCTS.FEATURES.DASHBOARD.BULLET_5',
      'PRODUCTS.FEATURES.DASHBOARD.BULLET_6',
    ],
    screenshot: productScreenshot('dashboard'),
    screenshotAltKey: 'PRODUCTS.FEATURES.DASHBOARD.SCREENSHOT_ALT',
    adminVisualBlocks: [
      visualBlock('dashboard', '1', 'dashboard'),
      visualBlock('dashboard', '2', 'dashboard-analytics'),
      visualBlock('dashboard', '3', 'dashboard-invoices'),
    ],
    detailIntroKey: 'PRODUCTS.DETAIL.DASHBOARD.INTRO',
    detailSections: [
      { id: 'ADMIN', itemCount: 0, portal: 'admin' },
      { id: 'RENTER', itemCount: 4, portal: 'renter' },
    ],
  },
  {
    id: 'properties',
    icon: 'lni-apartment',
    titleKey: 'PRODUCTS.FEATURES.PROPERTIES.TITLE',
    leadKey: 'PRODUCTS.FEATURES.PROPERTIES.LEAD',
    detailKey: 'PRODUCTS.FEATURES.PROPERTIES.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.PROPERTIES.BULLET_1',
      'PRODUCTS.FEATURES.PROPERTIES.BULLET_2',
      'PRODUCTS.FEATURES.PROPERTIES.BULLET_3',
      'PRODUCTS.FEATURES.PROPERTIES.BULLET_4',
      'PRODUCTS.FEATURES.PROPERTIES.BULLET_5',
      'PRODUCTS.FEATURES.PROPERTIES.BULLET_6',
    ],
    screenshot: productScreenshot('properties'),
    screenshotAltKey: 'PRODUCTS.FEATURES.PROPERTIES.SCREENSHOT_ALT',
    adminVisualBlocks: [
      visualBlock('properties', '1', 'properties'),
      visualBlock('properties', '2', 'properties-detail'),
    ],
    detailIntroKey: 'PRODUCTS.DETAIL.PROPERTIES.INTRO',
    detailSections: [
      { id: 'ADMIN', itemCount: 0, portal: 'admin' },
      { id: 'RENTER', itemCount: 4, portal: 'renter' },
    ],
  },
  {
    id: 'contracts',
    icon: 'lni-files',
    titleKey: 'PRODUCTS.FEATURES.CONTRACTS.TITLE',
    leadKey: 'PRODUCTS.FEATURES.CONTRACTS.LEAD',
    detailKey: 'PRODUCTS.FEATURES.CONTRACTS.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.CONTRACTS.BULLET_1',
      'PRODUCTS.FEATURES.CONTRACTS.BULLET_2',
      'PRODUCTS.FEATURES.CONTRACTS.BULLET_3',
      'PRODUCTS.FEATURES.CONTRACTS.BULLET_4',
      'PRODUCTS.FEATURES.CONTRACTS.BULLET_5',
      'PRODUCTS.FEATURES.CONTRACTS.BULLET_6',
    ],
    screenshot: productScreenshot('contracts'),
    screenshotAltKey: 'PRODUCTS.FEATURES.CONTRACTS.SCREENSHOT_ALT',
    adminVisualBlocks: [
      visualBlock('contracts', '1', 'contracts'),
      visualBlock('contracts', '2', 'contracts-edit'),
      visualBlock('contracts', '3', 'contracts-edit-reports'),
      visualBlock('contracts', '4', 'contracts-edit-documents'),
    ],
    detailIntroKey: 'PRODUCTS.DETAIL.CONTRACTS.INTRO',
    detailSections: [
      { id: 'ADMIN', itemCount: 0, portal: 'admin' },
      { id: 'RENTER', itemCount: 4, portal: 'renter' },
    ],
  },
  {
    id: 'expenses',
    icon: 'lni-receipt',
    titleKey: 'PRODUCTS.FEATURES.EXPENSES.TITLE',
    leadKey: 'PRODUCTS.FEATURES.EXPENSES.LEAD',
    detailKey: 'PRODUCTS.FEATURES.EXPENSES.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.EXPENSES.BULLET_1',
      'PRODUCTS.FEATURES.EXPENSES.BULLET_2',
      'PRODUCTS.FEATURES.EXPENSES.BULLET_3',
      'PRODUCTS.FEATURES.EXPENSES.BULLET_4',
      'PRODUCTS.FEATURES.EXPENSES.BULLET_5',
      'PRODUCTS.FEATURES.EXPENSES.BULLET_6',
    ],
    screenshot: productScreenshot('expenses'),
    screenshotAltKey: 'PRODUCTS.FEATURES.EXPENSES.SCREENSHOT_ALT',
    adminVisualBlocks: [
      visualBlock('expenses', '1', 'expenses'),
      visualBlock('expenses', '2', 'expenses-table'),
      visualBlock('expenses', '3', 'expenses-payment-reorg'),
    ],
    detailIntroKey: 'PRODUCTS.DETAIL.EXPENSES.INTRO',
    detailSections: [
      { id: 'ADMIN', itemCount: 0, portal: 'admin' },
      { id: 'RENTER', itemCount: 4, portal: 'renter' },
    ],
  },
  {
    id: 'utilities',
    icon: 'lni-stats-up',
    titleKey: 'PRODUCTS.FEATURES.UTILITIES.TITLE',
    leadKey: 'PRODUCTS.FEATURES.UTILITIES.LEAD',
    detailKey: 'PRODUCTS.FEATURES.UTILITIES.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.UTILITIES.BULLET_1',
      'PRODUCTS.FEATURES.UTILITIES.BULLET_2',
      'PRODUCTS.FEATURES.UTILITIES.BULLET_3',
      'PRODUCTS.FEATURES.UTILITIES.BULLET_4',
    ],
    screenshot: productScreenshot('utilities'),
    screenshotAltKey: 'PRODUCTS.FEATURES.UTILITIES.SCREENSHOT_ALT',
    detailIntroKey: 'PRODUCTS.DETAIL.UTILITIES.INTRO',
    detailSections: portalSections(),
  },
  {
    id: 'maintenance',
    icon: 'lni-construction-hammer',
    titleKey: 'PRODUCTS.FEATURES.MAINTENANCE.TITLE',
    leadKey: 'PRODUCTS.FEATURES.MAINTENANCE.LEAD',
    detailKey: 'PRODUCTS.FEATURES.MAINTENANCE.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.MAINTENANCE.BULLET_1',
      'PRODUCTS.FEATURES.MAINTENANCE.BULLET_2',
      'PRODUCTS.FEATURES.MAINTENANCE.BULLET_3',
      'PRODUCTS.FEATURES.MAINTENANCE.BULLET_4',
    ],
    screenshot: productScreenshot('maintenance'),
    screenshotAltKey: 'PRODUCTS.FEATURES.MAINTENANCE.SCREENSHOT_ALT',
    detailIntroKey: 'PRODUCTS.DETAIL.MAINTENANCE.INTRO',
    detailSections: portalSections(),
  },
  {
    id: 'accounting',
    icon: 'lni-calculator',
    titleKey: 'PRODUCTS.FEATURES.ACCOUNTING.TITLE',
    leadKey: 'PRODUCTS.FEATURES.ACCOUNTING.LEAD',
    detailKey: 'PRODUCTS.FEATURES.ACCOUNTING.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.ACCOUNTING.BULLET_1',
      'PRODUCTS.FEATURES.ACCOUNTING.BULLET_2',
      'PRODUCTS.FEATURES.ACCOUNTING.BULLET_3',
      'PRODUCTS.FEATURES.ACCOUNTING.BULLET_4',
    ],
    screenshot: productScreenshot('accounting'),
    screenshotAltKey: 'PRODUCTS.FEATURES.ACCOUNTING.SCREENSHOT_ALT',
    detailIntroKey: 'PRODUCTS.DETAIL.ACCOUNTING.INTRO',
    detailSections: portalSections(),
  },
  {
    id: 'fleet',
    icon: 'lni-car',
    titleKey: 'PRODUCTS.FEATURES.FLEET.TITLE',
    leadKey: 'PRODUCTS.FEATURES.FLEET.LEAD',
    detailKey: 'PRODUCTS.FEATURES.FLEET.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.FLEET.BULLET_1',
      'PRODUCTS.FEATURES.FLEET.BULLET_2',
      'PRODUCTS.FEATURES.FLEET.BULLET_3',
      'PRODUCTS.FEATURES.FLEET.BULLET_4',
    ],
    screenshot: productScreenshot('fleet'),
    screenshotAltKey: 'PRODUCTS.FEATURES.FLEET.SCREENSHOT_ALT',
    detailIntroKey: 'PRODUCTS.DETAIL.FLEET.INTRO',
    detailSections: portalSections(),
  },
  {
    id: 'actors',
    icon: 'lni-users',
    titleKey: 'PRODUCTS.FEATURES.ACTORS.TITLE',
    leadKey: 'PRODUCTS.FEATURES.ACTORS.LEAD',
    detailKey: 'PRODUCTS.FEATURES.ACTORS.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.ACTORS.BULLET_1',
      'PRODUCTS.FEATURES.ACTORS.BULLET_2',
      'PRODUCTS.FEATURES.ACTORS.BULLET_3',
      'PRODUCTS.FEATURES.ACTORS.BULLET_4',
    ],
    screenshot: productScreenshot('actors'),
    screenshotAltKey: 'PRODUCTS.FEATURES.ACTORS.SCREENSHOT_ALT',
    detailIntroKey: 'PRODUCTS.DETAIL.ACTORS.INTRO',
    detailSections: portalSections(),
  },
  {
    id: 'profile',
    icon: 'lni-user',
    titleKey: 'PRODUCTS.FEATURES.PROFILE.TITLE',
    leadKey: 'PRODUCTS.FEATURES.PROFILE.LEAD',
    detailKey: 'PRODUCTS.FEATURES.PROFILE.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.PROFILE.BULLET_1',
      'PRODUCTS.FEATURES.PROFILE.BULLET_2',
      'PRODUCTS.FEATURES.PROFILE.BULLET_3',
      'PRODUCTS.FEATURES.PROFILE.BULLET_4',
      'PRODUCTS.FEATURES.PROFILE.BULLET_5',
      'PRODUCTS.FEATURES.PROFILE.BULLET_6',
    ],
    screenshot: productScreenshot('profile'),
    screenshotAltKey: 'PRODUCTS.FEATURES.PROFILE.SCREENSHOT_ALT',
    adminVisualBlocks: [
      visualBlock('profile', '1', 'profile'),
      visualBlock('profile', '2', 'notifications-dropdown'),
      visualBlock('profile', '3', 'reminders'),
    ],
    detailIntroKey: 'PRODUCTS.DETAIL.PROFILE.INTRO',
    detailSections: [
      { id: 'ADMIN', itemCount: 0, portal: 'admin' },
      { id: 'NOTIFICATION_TYPES', itemCount: 6 },
      { id: 'RENTER', itemCount: 4, portal: 'renter' },
    ],
  },
  {
    id: 'organisation',
    icon: 'lni-briefcase',
    titleKey: 'PRODUCTS.FEATURES.ORGANISATION.TITLE',
    leadKey: 'PRODUCTS.FEATURES.ORGANISATION.LEAD',
    detailKey: 'PRODUCTS.FEATURES.ORGANISATION.DETAIL',
    bulletKeys: [
      'PRODUCTS.FEATURES.ORGANISATION.BULLET_1',
      'PRODUCTS.FEATURES.ORGANISATION.BULLET_2',
      'PRODUCTS.FEATURES.ORGANISATION.BULLET_3',
      'PRODUCTS.FEATURES.ORGANISATION.BULLET_4',
      'PRODUCTS.FEATURES.ORGANISATION.BULLET_5',
      'PRODUCTS.FEATURES.ORGANISATION.BULLET_6',
    ],
    screenshot: productScreenshot('organisation'),
    screenshotAltKey: 'PRODUCTS.FEATURES.ORGANISATION.SCREENSHOT_ALT',
    adminVisualBlocks: [
      visualBlock('organisation', '1', 'organisation'),
      visualBlock('organisation', '2', 'organisation-details'),
      visualBlock('organisation', '3', 'organisation-configuration'),
      visualBlock('organisation', '4', 'organisation-accounting'),
      visualBlock('organisation', '5', 'organisation-emails'),
    ],
    detailIntroKey: 'PRODUCTS.DETAIL.ORGANISATION.INTRO',
    detailSections: [
      { id: 'ADMIN', itemCount: 0, portal: 'admin' },
      { id: 'RENTER', itemCount: 4, portal: 'renter' },
    ],
  },
];

export function getProductFeature(id: string): ProductFeature | undefined {
  return PRODUCT_FEATURES.find((f) => f.id === id);
}

export function getAdjacentFeatures(id: string): { prev?: ProductFeature; next?: ProductFeature } {
  const index = PRODUCT_FEATURES.findIndex((f) => f.id === id);
  if (index < 0) {
    return {};
  }
  return {
    prev: index > 0 ? PRODUCT_FEATURES[index - 1] : undefined,
    next: index < PRODUCT_FEATURES.length - 1 ? PRODUCT_FEATURES[index + 1] : undefined,
  };
}

export function buildDetailSectionKeys(
  featureId: string,
  section: ProductDetailSection
): { titleKey: string; introKey: string; items: { titleKey: string; textKey: string }[] } {
  const base = `PRODUCTS.DETAIL.${featureId.toUpperCase()}.SECTIONS.${section.id}`;
  const items = Array.from({ length: section.itemCount }, (_, i) => ({
    titleKey: `${base}.ITEM_${i + 1}_TITLE`,
    textKey: `${base}.ITEM_${i + 1}_TEXT`,
  }));
  return {
    titleKey: `${base}.TITLE`,
    introKey: `${base}.INTRO`,
    items,
  };
}
